import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NavigationItem } from '@app/core/types/navigation.interface';
import { MenuItemVerticalComponent } from '../menu-item/menu-item.component';
import { AuthenticationService } from '@app/core/auth';
import { MenuStateService } from '@app/core/services/menu-state.service';
import { MENU_ICON_MAP } from '@app/core/const';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-menu-collapse',
  imports: [RouterModule, MenuItemVerticalComponent, MenuCollapseComponent, TranslatePipe, MatIconModule],
  template: `
    @if (item() && !item()?.hidden) {
      <li
        class="nav-item coded-hasmenu"
        [class.disabled]="!isEnabled()"
        [class.coded-trigger]="isMenuExpanded()"
        [class.active]="isMenuExpanded()"
        [attr.matTooltip]="tooltipText()"
        [attr.data-menu-id]="item()?.id"
        [attr.data-menu-title]="item()?.title"
      >
        <a class="nav-link menu-collapse__link" (click)="toggleCollapse()">
          <div class="menu-collapse__content">
            @if (item()?.icon) {
              <span class="menu-collapse__icon">
                <mat-icon>{{ iconName() }}</mat-icon>
              </span>
            }
            <span class="coded-mtext">{{ item()?.title | translate }}</span>
          </div>
          <mat-icon class="expand-icon" [attr.matTooltip]="expandTooltip()">
            {{ isMenuExpanded() ? 'expand_less' : 'expand_more' }}
          </mat-icon>
        </a>

        <ul class="coded-submenu" [@slideInOut]="isMenuExpanded()">
          @if (item()?.children) {
            @for (child of item()!.children; track child.id) {
              @if (child.type === 'item') {
                <app-menu-item
                  [item]="child"
                  [level]="level() + 1"
                  [parentId]="item()?.id ?? ''"
                  [parentRole]="item()?.role?.length ? item()!.role : parentRole()"
                />
              } @else if (child.type === 'collapsible') {
                <app-menu-collapse
                  [item]="child"
                  [level]="level() + 1"
                  [parentId]="item()?.id ?? ''"
                  [parentRole]="item()?.role?.length ? item()!.role : parentRole()"
                />
              }
            }
          }
        </ul>
      </li>
    }
  `,
  styles: [
    `
      .menu-collapse {
        &__link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1.25rem;
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;

          &--active {
            color: var(--primary-500);
            font-weight: 500;

            .menu-collapse__icon,
            .expand-icon {
              color: var(--primary-500);
            }
          }

          &[aria-disabled='true'] {
            opacity: 0.6;
            pointer-events: none;
          }

          &:focus-visible {
            outline: 2px dashed var(--primary-500);
            outline-offset: -2px;
          }
        }

        &__content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        &__icon,
        &__text {
          display: flex;
          align-items: center;
          font-size: clamp(0.875rem, 0.8rem + 0.25vw, 1rem);
          margin-left: clamp(0.5rem, 0.25rem + 0.5vw, 1rem);
          transition: color 0.3s ease;
        }

        &__submenu {
          list-style: none;
          padding-left: 1rem;
          margin: 0;
          overflow: hidden;
        }
      }

      .expand-icon {
        transition: transform 0.3s ease;
        .menu-collapse__link--active & {
          transform: rotate(180deg);
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      state(
        'true',
        style({
          overflow: 'hidden',
          height: '*',
          opacity: 1,
          visibility: 'visible',
          transform: 'rotateX(0deg)'
        })
      ),
      state(
        'false',
        style({
          overflow: 'hidden',
          height: '0px',
          opacity: 0,
          visibility: 'hidden',
          transform: 'rotateX(-90deg)'
        })
      ),
      transition('false <=> true', animate('40ms cubic-bezier(0.35, 0, 0.25, 1)'))
    ])
  ]
})
export class MenuCollapseComponent {
  private readonly authService = inject(AuthenticationService);
  private readonly menuStateService = inject(MenuStateService);

  readonly item = input.required<NavigationItem>();
  readonly parentRole = input<string[]>([]);
  readonly level = input(0);

  readonly menuId = computed(() => this.item().id);

  readonly isEnabled = computed(() => {
    const menuItem = this.item();
    const userRole = this.authService.currentUser()?.user.role;
    if (!userRole || !menuItem.role?.length) return true;
    return menuItem.role.includes(userRole);
  });

  readonly isMenuExpanded = computed(() => this.menuStateService.isMenuOpen()(this.menuId()));
  readonly tooltipText = computed(() => (this.isEnabled() ? '' : 'No tienes permiso para acceder.'));
  readonly expandTooltip = computed(() => (this.isMenuExpanded() ? 'Contraer' : 'Expandir'));

  readonly iconName = computed(() => {
    const menuItem = this.item();
    const title = menuItem.title;
    const iconPath = menuItem.icon;

    if (title && MENU_ICON_MAP[title]) {
      return MENU_ICON_MAP[title];
    }
    if (!iconPath) {
      return 'help';
    }
    if (iconPath.startsWith('#custom-')) {
      return iconPath.substring(8);
    }
    const parts = iconPath.split('/');
    const filename = parts[parts.length - 1];
    return filename.replace('.svg', '');
  });

  toggleCollapse(): void {
    if (this.isEnabled()) {
      this.menuStateService.toggleMenu(this.menuId());
    }
  }
}
